import moment from "moment";
import {
  AuthAuditEventType,
  EmployeeActivityEventType,
  EmployeeActivityMetadata,
  LegacyDockLogRow,
  ScanEventType,
  ScanLog,
} from "./models";

const EVENT_TYPE_LABELS: Record<ScanEventType, string> = {
  ITEM_PICKED_FOR_LOADING: "Picked",
  ITEM_LOADED: "Loaded",
  ITEM_STAGED: "Staged",
  ITEM_RACKED: "Racked",
  ITEM_MARKED_OVERAGE: "Overage",
  ITEM_MARKED_SHORTAGE: "Shortage",
  ITEM_UNLOADED: "Unloaded",
  WRONG_DOOR_SCAN: "Wrong Door",
  LOCATION_RECOMMENDED: "Loc Recommended",
  FREIGHT_DAMAGE_REPORTED: "Damage Reported",
  VALIDATION_FAILED: "Validation X",
  WORKFLOW_REJECTED: "Workflow Rejected",
  DUPLICATE_SUPPRESSED: "Duplicate",
  SCAN_EXCEPTION: "Scan Exception",
  NONE: "Scan",
};

const AUTH_EVENT_TYPE_LABELS: Record<AuthAuditEventType, string> = {
  LOGIN_SUCCESS: "Login OK",
  LOGIN_DENIED_INVALID_ID: "Invalid ID",
  LOGIN_DENIED_INACTIVE: "Inactive",
  LOGOUT: "Logout",
  ASSIGNMENT_CREATED: "Assign Created",
  ASSIGNMENT_DENIED: "Assign Denied",
  ASSIGNMENT_CLOSED: "Assign Closed",
};

export function probillToBarcodePrefix(probill: number): string {
  return `P${String(probill).padStart(8, "0")}`;
}

export function formatFreightBarcode(barcodeValue: string): string {
  if (!barcodeValue) return "";
  const match = barcodeValue.match(/^P(\d{8})(\d{4})$/i);
  if (match) {
    return `P${match[1]}${match[2]}`;
  }
  return barcodeValue;
}

export function formatDoorDisplay(doorId?: string | null): string {
  if (!doorId) return "";
  return doorId.startsWith("D") ? doorId : `D${doorId}`;
}

export function humanizeEventType(eventType?: ScanEventType): string {
  if (!eventType) return "Scan";
  return EVENT_TYPE_LABELS[eventType] || eventType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function humanizeAuthEventType(eventType?: AuthAuditEventType): string {
  if (!eventType) return "Event";
  return (
    AUTH_EVENT_TYPE_LABELS[eventType] ||
    eventType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function humanizeScanMode(scanMode?: string | null): string {
  if (!scanMode) return "—";
  return scanMode
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function authEventPillVariant(
  eventType: AuthAuditEventType
): "success" | "failure" | "warning" | "info" | "neutral" {
  switch (eventType) {
    case "LOGIN_SUCCESS":
      return "success";
    case "LOGIN_DENIED_INVALID_ID":
      return "failure";
    case "LOGIN_DENIED_INACTIVE":
      return "warning";
    case "LOGOUT":
      return "neutral";
    case "ASSIGNMENT_CREATED":
    case "ASSIGNMENT_CLOSED":
      return "info";
    case "ASSIGNMENT_DENIED":
      return "failure";
    default:
      return "neutral";
  }
}

const EMPLOYEE_ACTIVITY_LABELS: Record<EmployeeActivityEventType, string> = {
  EMPLOYEE_CREATED: "Created",
  EMPLOYEE_UPDATED: "Updated",
  EMPLOYEE_DEACTIVATED: "Deactivated",
  EMPLOYEE_TAGS_UPDATED: "Tags updated",
  TAG_CREATED: "Tag created",
  TAG_DELETED: "Tag deleted",
};

const ACTIVITY_FIELD_LABELS: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  email: "Email",
  terminalNumber: "Home terminal",
  status: "Status",
  tagIds: "Tags",
  tags: "Tags",
};

export function humanizeEmployeeActivityEventType(eventType?: EmployeeActivityEventType): string {
  if (!eventType) return "Event";
  return (
    EMPLOYEE_ACTIVITY_LABELS[eventType] ||
    eventType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function employeeActivityPillVariant(
  eventType: EmployeeActivityEventType
): "success" | "failure" | "warning" | "info" | "neutral" {
  switch (eventType) {
    case "EMPLOYEE_CREATED":
    case "EMPLOYEE_UPDATED":
      return "info";
    case "EMPLOYEE_DEACTIVATED":
      return "warning";
    case "EMPLOYEE_TAGS_UPDATED":
    case "TAG_CREATED":
    case "TAG_DELETED":
      return "neutral";
    default:
      return "neutral";
  }
}

function formatActivityItem(item: unknown): string {
  if (item == null) return "—";
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  if (typeof item === "object") {
    const rec = item as { name?: unknown; label?: unknown; id?: unknown };
    if (rec.name != null && String(rec.name).trim() !== "") return String(rec.name);
    if (rec.label != null && String(rec.label).trim() !== "") return String(rec.label);
    if (rec.id != null) return String(rec.id);
    return JSON.stringify(item);
  }
  return String(item);
}

function formatActivityValue(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) {
    return value.length ? value.map(formatActivityItem).join(", ") : "None";
  }
  if (typeof value === "object") {
    return formatActivityItem(value);
  }
  return String(value);
}

function activityFieldLabel(key: string): string {
  if (ACTIVITY_FIELD_LABELS[key]) return ACTIVITY_FIELD_LABELS[key];
  const spaced = key.replace(/([A-Z])/g, " $1").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function summarizeEmployeeActivityChanges(metadata?: EmployeeActivityMetadata): string {
  const previous = metadata?.previous;
  const next = metadata?.next;
  if (!previous && !next) return "—";

  const keys = Object.keys({ ...(previous || {}), ...(next || {}) });
  const parts: string[] = [];

  keys.forEach((key) => {
    const from = previous ? previous[key] : undefined;
    const to = next ? next[key] : undefined;
    if (JSON.stringify(from) === JSON.stringify(to)) return;

    const label = activityFieldLabel(key);
    if (from == null) {
      parts.push(`${label} ${formatActivityValue(to)}`);
    } else {
      parts.push(`${label} ${formatActivityValue(from)} → ${formatActivityValue(to)}`);
    }
  });

  return parts.length ? parts.join("; ") : "—";
}

export function isFailureEvent(eventType?: ScanEventType, resultStatus?: string): boolean {
  if (resultStatus === "FAILURE") return true;
  return eventType === "WRONG_DOOR_SCAN" || eventType === "WORKFLOW_REJECTED" || eventType === "VALIDATION_FAILED";
}

export function scanEventPillVariant(
  eventType: ScanEventType,
  resultStatus?: string
): "success" | "failure" | "warning" | "info" | "neutral" | "inactive" | "live" {
  if (isFailureEvent(eventType, resultStatus)) {
    if (eventType === "WRONG_DOOR_SCAN") return "warning";
    return "failure";
  }
  switch (eventType) {
    case "ITEM_LOADED":
    case "ITEM_STAGED":
    case "ITEM_RACKED":
    case "ITEM_UNLOADED":
      return "success";
    case "ITEM_MARKED_OVERAGE":
    case "ITEM_MARKED_SHORTAGE":
      return "warning";
    case "ITEM_PICKED_FOR_LOADING":
      return "info";
    case "LOCATION_RECOMMENDED":
      return "live";
    case "DUPLICATE_SUPPRESSED":
      return "inactive";
    default:
      return "info";
  }
}

function parsePieceSequenceFromBarcode(barcodeValue: string): string {
  const match = barcodeValue.match(/^P\d{8}(\d{4})$/i);
  return match ? match[1] : "";
}

function parseDoorFromScanLog(log: ScanLog): string {
  if (log.recommendedDoorId) {
    return formatDoorDisplay(log.recommendedDoorId);
  }
  if (log.barcodeType === "DOOR") {
    return formatDoorDisplay(log.barcodeValue);
  }
  if (log.recommendedLocation?.locationCode && log.recommendedLocation.locationType === "DOOR") {
    return formatDoorDisplay(log.recommendedLocation.locationCode);
  }
  return "";
}

function getScanTimestamp(log: ScanLog): string {
  return log.scannedAt || log.createdAt;
}

export function scanLogToLegacyDockLogRow(
  log: ScanLog,
  terminalFallback?: { id: number | string; name: string }
): LegacyDockLogRow {
  const timestamp = getScanTimestamp(log);
  const m = moment(timestamp);
  const terminalNumber =
    log.recommendedLocation?.terminalNumber ??
    (terminalFallback?.id != null ? Number(terminalFallback.id) : undefined);

  return {
    date: timestamp,
    time: m.isValid() ? m.format("h:mm A") : "",
    event: humanizeEventType(log.eventType),
    serviceCenter: {
      id: terminalNumber ?? terminalFallback?.id ?? "",
      name: terminalFallback?.name ?? "",
    },
    door: parseDoorFromScanLog(log),
    orderOfItem: parsePieceSequenceFromBarcode(log.barcodeValue),
    rollNumber: log.barcodeType === "ROLL_NUMBER" ? log.barcodeValue : "",
    trailer:
      log.recommendedTrailerId ??
      (log.recommendedLocation?.trailerNumber != null
        ? String(log.recommendedLocation.trailerNumber)
        : ""),
    message: log.resultMessage || log.resultCode || "",
    _sortTimestamp: m.isValid() ? m.valueOf() : 0,
    _source: "scanning-service",
  };
}

export function mergeAndSortDockLogs(
  legacyRows: LegacyDockLogRow[],
  scanRows: LegacyDockLogRow[]
): LegacyDockLogRow[] {
  const withSort = (rows: LegacyDockLogRow[], source: "legacy" | "scanning-service") =>
    rows.map((row) => ({
      ...row,
      _source: row._source || source,
      _sortTimestamp:
        row._sortTimestamp ??
        (row.date ? moment(row.date).valueOf() : 0),
    }));

  return [...withSort(legacyRows, "legacy"), ...withSort(scanRows, "scanning-service")].sort(
    (a, b) => (b._sortTimestamp ?? 0) - (a._sortTimestamp ?? 0)
  );
}

export function formatSessionDuration(ms: number): string {
  if (ms <= 0) return "0m";
  const duration = moment.duration(ms);
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
